import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  Avatar,
  Tabs,
  Tab,
  Chip,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { projectService, Project, ProjectUpdateData } from '../services/projectService';
import { userService } from '../services/userService';
import { useThemeMode } from '../contexts/ThemeContext';
import KYCStatusCard from '../components/kyc/KYCStatusCard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface User {
  id: string;
  wallet_address: string;
  role: string;
  created_at?: string;
}

const Profile = () => {
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const [projects, setProjects] = useState<Project[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [editFormData, setEditFormData] = useState<ProjectUpdateData>({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submittingProjects, setSubmittingProjects] = useState<Set<string>>(new Set());

  const projectTypes = [
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'art', label: 'Art' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'agriculture', label: 'Agriculture' },
  ];

  useEffect(() => {
    if (isConnected && address) {
      fetchUserData();
    } else {
      setError('Please connect your wallet to view your profile');
      setLoading(false);
    }
  }, [isConnected, address]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch user data
      const userData = await userService.getUserByWallet(address!);
      setUser(userData);

      // Fetch user's projects
      const response = await projectService.getProjects();
      // Filter projects by user_id (in a real app, this would be done on the backend)
      const userProjects = response.projects.filter(project => 
        project.user_id === userData.id
      );
      setProjects(userProjects);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getProjectsByStatus = (status: string) => {
    return projects.filter(project => project.status === status);
  };

  const getStatusStats = () => {
    const stats = {
      draft: getProjectsByStatus('draft').length,
      submitted: getProjectsByStatus('submitted').length,
      approved: getProjectsByStatus('approved').length,
      tokenized: getProjectsByStatus('tokenized').length,
      rejected: getProjectsByStatus('rejected').length,
    };

    return [
      {
        label: 'Draft Projects',
        value: stats.draft,
        icon: PendingIcon,
        color: 'text.secondary',
      },
      {
        label: 'Submitted',
        value: stats.submitted,
        icon: ScheduleIcon,
        color: 'warning.main',
      },
      {
        label: 'Approved',
        value: stats.approved,
        icon: CheckCircleIcon,
        color: 'info.main',
      },
      {
        label: 'Tokenized',
        value: stats.tokenized,
        icon: TrendingUpIcon,
        color: 'success.main',
      },
    ];
  };

  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setEditFormData({
      title: project.title || '',
      description: project.description || '',
      location: project.location || '',
      valuation_usd: project.valuation_usd || 0,
      tokenized_percent: project.tokenized_percent || 100,
      project_type: project.project_type,
      token_name: project.token_name || '',
      token_symbol: project.token_symbol || '',
    });
    setEditDialogOpen(true);
  };

  const handleSubmitProject = async (project: Project) => {
    try {
      setSubmittingProjects(prev => new Set(prev).add(project.id));
      setError(null);
      
      // Update project status from 'draft' to 'submitted'
      await projectService.updateProject(project.id, { status: 'submitted' });
      
      // Refresh projects to show updated status
      await fetchUserData();
      
      setSuccessMessage('Project submitted successfully!');
      
    } catch (error) {
      console.error('Failed to submit project:', error);
      setError('Failed to submit project. Please try again.');
    } finally {
      setSubmittingProjects(prev => {
        const newSet = new Set(prev);
        newSet.delete(project.id);
        return newSet;
      });
    }
  };

  const handleUpdateProject = async () => {
    if (!selectedProject) return;

    try {
      setSubmitting(true);
      await projectService.updateProject(selectedProject.id, editFormData);
      
      // Refresh projects
      await fetchUserData();
      
      setEditDialogOpen(false);
      setSelectedProject(null);
      setEditFormData({});
      setError(null);
      setSuccessMessage('Project updated successfully!');
    } catch (error) {
      console.error('Failed to update project:', error);
      setError('Failed to update project. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderProjectGrid = (projectList: Project[]) => {
    if (loading) {
      return (
        <Grid container spacing={3}>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} md={4} key={item}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="40%" height={24} />
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="text" width="80%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }

    if (projectList.length === 0) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No projects found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {tabValue === 0 && 'You haven\'t created any projects yet.'}
              {tabValue === 1 && 'No projects are currently submitted for review.'}
              {tabValue === 2 && 'No projects have been approved yet.'}
              {tabValue === 3 && 'No projects have been tokenized yet.'}
            </Typography>
            {tabValue === 0 && (
              <Button
                variant="contained"
                onClick={() => navigate('/submit')}
                sx={{
                  background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)',
                  },
                }}
              >
                Create New Project
              </Button>
            )}
          </CardContent>
        </Card>
      );
    }

    return (
      <Grid container spacing={3}>
        {projectList.map((project, index) => (
          <Grid item xs={12} md={4} key={project.id}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card
                sx={{
                  background: 'rgba(17, 24, 39, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  position: 'relative',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                      {project.title || 'Untitled Project'}
                    </Typography>
                    <Chip
                      label={project.status}
                      color={project.status === 'draft' ? 'default' : 
                             project.status === 'submitted' ? 'warning' :
                             project.status === 'approved' ? 'info' :
                             project.status === 'tokenized' ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {project.description || 'No description available'}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Valuation: ${project.valuation_usd?.toLocaleString() || '0'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {project.tokenized_percent}% Tokenized
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                         {project.status === 'draft' && (
                       <>
                         <Tooltip title="Edit Project">
                           <IconButton
                             size="small"
                             onClick={() => handleEditProject(project)}
                             sx={{ color: 'primary.main' }}
                           >
                             <EditIcon />
                           </IconButton>
                         </Tooltip>
                                                                             <Tooltip title="Submit Project for Review">
                             <span>
                               <Button
                                 size="small"
                                 variant="outlined"
                                 startIcon={<SendIcon />}
                                 disabled={submittingProjects.has(project.id)}
                                 onClick={() => handleSubmitProject(project)}
                                 sx={{
                                   borderColor: 'primary.main',
                                   color: 'primary.main',
                                   '&:hover': {
                                     borderColor: 'primary.dark',
                                     backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                   },
                                 }}
                               >
                                 {submittingProjects.has(project.id) ? 'Submitting...' : 'Submit'}
                               </Button>
                             </span>
                           </Tooltip>
                       </>
                     )}
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/project/${project.id}`)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    );
  };

  if (!isConnected) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          py: 4,
          background: isDark
            ? 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(14, 165, 233, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(30, 64, 175, 0.03) 0%, rgba(14, 165, 233, 0.03) 100%)',
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="xl">
          <Alert
            severity="warning"
            sx={{
              background: isDark ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.08)',
              border: `1px solid ${isDark ? 'rgba(245, 158, 11, 0.3)' : 'rgba(245, 158, 11, 0.2)'}`,
            }}
          >
            Please connect your wallet to view your profile.
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
        background: isDark
          ? 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(14, 165, 233, 0.05) 100%)'
          : 'linear-gradient(135deg, rgba(30, 64, 175, 0.03) 0%, rgba(14, 165, 233, 0.03) 100%)',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="xl">
        {/* Profile Header */}
        <Card
          sx={{
            mb: 4,
            background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                    fontSize: '2rem',
                    boxShadow: '0 8px 32px rgba(30, 64, 175, 0.3)',
                  }}
                >
                  <PersonIcon sx={{ fontSize: '2rem' }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    background: 'linear-gradient(135deg, #3B82F6 0%, #0EA5E9 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {user?.wallet_address ? `${user.wallet_address.slice(0, 6)}...${user.wallet_address.slice(-4)}` : 'User Profile'}
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  {user?.role === 'issuer' ? 'Asset Issuer' : 'User'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<BusinessIcon />}
                  onClick={() => navigate('/submit')}
                  sx={{
                    borderColor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.5)',
                    color: '#3B82F6',
                    '&:hover': {
                      borderColor: '#3B82F6',
                      background: 'rgba(59, 130, 246, 0.1)',
                    },
                  }}
                >
                  Submit New Project
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

             {error && (
         <Alert severity="error" sx={{ mb: 3 }}>
           {error}
         </Alert>
       )}

               {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {/* KYC Status Card */}
        {user?.id && <KYCStatusCard userId={user.id} />}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {getStatusStats().map((stat, index) => (
            <Grid item xs={12} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                    borderRadius: 3,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <stat.icon
                        sx={{
                          fontSize: '2rem',
                          color: stat.color,
                          mr: 2,
                        }}
                      />
                      <Box>
                        <Typography variant="h4" fontWeight={700} color={stat.color}>
                          {stat.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {stat.label}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Projects Tabs */}
        <Card
          sx={{
            background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            borderRadius: 3,
          }}
        >
          <CardContent>
            <Box sx={{ borderBottom: 1, borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: '#3B82F6',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    background: 'linear-gradient(90deg, #3B82F6, #0EA5E9)',
                  },
                }}
              >
                <Tab label="Draft" />
                <Tab label="Submitted" />
                <Tab label="Approved" />
                <Tab label="Tokenized" />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {renderProjectGrid(getProjectsByStatus('draft'))}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {renderProjectGrid(getProjectsByStatus('submitted'))}
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              {renderProjectGrid(getProjectsByStatus('approved'))}
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              {renderProjectGrid(getProjectsByStatus('tokenized'))}
            </TabPanel>
          </CardContent>
        </Card>

      {/* Edit Project Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Edit Project</Typography>
            <IconButton onClick={() => setEditDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project Title"
                value={editFormData.title || ''}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={editFormData.description || ''}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Location"
                value={editFormData.location || ''}
                onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Project Type</InputLabel>
                <Select
                  value={editFormData.project_type || 'real_estate'}
                  onChange={(e) => setEditFormData({ ...editFormData, project_type: e.target.value as any })}
                  label="Project Type"
                >
                  {projectTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Valuation (USD)"
                type="number"
                value={editFormData.valuation_usd || ''}
                onChange={(e) => setEditFormData({ ...editFormData, valuation_usd: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tokenized Percentage"
                type="number"
                value={editFormData.tokenized_percent || ''}
                onChange={(e) => setEditFormData({ ...editFormData, tokenized_percent: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Token Name"
                value={editFormData.token_name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, token_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Token Symbol"
                value={editFormData.token_symbol || ''}
                onChange={(e) => setEditFormData({ ...editFormData, token_symbol: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateProject}
            variant="contained"
            disabled={submitting}
            sx={{
              background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)',
              },
            }}
          >
            {submitting ? 'Updating...' : 'Update Project'}
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  );
};

export default Profile; 